/**
 * /projects/:projectSlug — environments for one project.
 *
 * Resolves the project record (404 → "not found" state), lists its envs, and
 * offers a Create button. Each env row links to
 * /projects/:projectSlug/envs/:envSlug/secrets.
 *
 * The project and envs queries both key off the URL slug, so they run in
 * parallel and we gate on a single combined skeleton — no second flash when
 * the list resolves after the header.
 */

import { PageHeader } from "@/components/PageHeader";
import {
  CreateEnvModal,
  DeleteProjectModal,
  EditProjectModal,
} from "@/components/ProjectEnvModals";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { WardkeyAPIError, useActiveOrg, useApiQuery } from "@/lib/api";
import type { EnvironmentRecord, ProjectRecord } from "@wardkey/sdk";
import { ArrowRight, Layers } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

type ModalKind = null | "env" | "edit" | "delete";

export function ProjectDetailRoute() {
  const activeOrg = useActiveOrg();
  const params = useParams<{ projectSlug: string }>();
  const projectSlug = params.projectSlug ?? "";
  const enabled = projectSlug.length > 0;

  const project = useApiQuery(
    ["project", activeOrg.org_id, projectSlug],
    (client) => client.projects.get(activeOrg.org_id, projectSlug),
    { enabled, suppressToast: true },
  );

  // Keyed off the URL slug (not the loaded record) so it fetches in parallel
  // with the project — both resolve together behind one skeleton.
  const envs = useApiQuery(
    ["envs", activeOrg.org_id, projectSlug],
    (client) => client.envs.list(activeOrg.org_id, projectSlug),
    { enabled, suppressToast: true },
  );

  // Only the *initial* load gets a skeleton / error page — background refetches
  // (after rename/delete) keep the current content rather than flashing, since
  // `isLoading` flips back to true on every refetch while `data` is retained.
  if (project.error && !project.data) {
    return (
      <ProjectErrorState
        error={project.error}
        onRetry={() => {
          project.refetch();
          envs.refetch();
        }}
      />
    );
  }
  // One skeleton until both have landed (an envs error counts as settled — the
  // list surfaces it inline once the page renders).
  if (!project.data || (envs.isLoading && !envs.data)) {
    return <ProjectDetailSkeleton />;
  }

  return (
    <ProjectDetail
      orgName={activeOrg.name}
      project={project.data}
      envItems={envs.data?.environments ?? []}
      envsError={envs.error}
      onRefetchEnvs={() => envs.refetch()}
      onProjectChanged={() => {
        project.refetch();
        envs.refetch();
      }}
    />
  );
}

function ProjectDetail({
  orgName,
  project,
  envItems,
  envsError,
  onRefetchEnvs,
  onProjectChanged,
}: {
  orgName: string;
  project: ProjectRecord;
  envItems: ReadonlyArray<EnvironmentRecord>;
  envsError: Error | null;
  onRefetchEnvs: () => void;
  onProjectChanged: () => void;
}) {
  const navigate = useNavigate();
  const [modal, setModal] = useState<ModalKind>(null);
  const hasEnvs = envItems.length > 0;

  return (
    <div className="fade-up mx-auto max-w-[1200px]">
      <PageHeader
        title={project.display_name || project.project_slug}
        adornment={
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            onClick={() => setModal("edit")}
            aria-label="Rename project"
            title="Rename project"
          >
            <PencilIcon className="h-3.5 w-3.5" />
          </Button>
        }
        subtitle={
          <span className="font-mono text-[12px] text-muted-foreground">
            {orgName} / {project.project_slug}
          </span>
        }
        actions={
          <Button type="button" size="sm" onClick={() => setModal("env")}>
            New environment
            <span aria-hidden>→</span>
          </Button>
        }
      />

      <div className="section-heading">
        <h2>Environments</h2>
        <span>Separate secrets for every stage</span>
      </div>
      <EnvsList
        error={envsError}
        items={envItems}
        projectSlug={project.project_slug}
        onRefetch={onRefetchEnvs}
        onCreate={() => setModal("env")}
      />

      <DangerZone project={project} hasEnvs={hasEnvs} onDelete={() => setModal("delete")} />

      {modal === "env" && (
        <CreateEnvModal
          project={project}
          onClose={() => setModal(null)}
          onCreated={(record) => {
            setModal(null);
            navigate(
              `/projects/${encodeURIComponent(project.project_slug)}/envs/${encodeURIComponent(
                record.env_slug,
              )}/secrets`,
            );
          }}
        />
      )}
      {modal === "edit" && (
        <EditProjectModal
          project={project}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            onProjectChanged();
          }}
        />
      )}
      {modal === "delete" && (
        <DeleteProjectModal
          project={project}
          onClose={() => setModal(null)}
          onDeleted={() => {
            setModal(null);
            navigate("/projects");
          }}
        />
      )}
    </div>
  );
}

/**
 * Legible in both themes: a neutral card surface framed by a clear red border
 * (the faint red-on-red tint washed out in light mode), a red label, and the
 * red Delete button carrying the danger signal.
 */
function DangerZone({
  project,
  hasEnvs,
  onDelete,
}: {
  project: ProjectRecord;
  hasEnvs: boolean;
  onDelete: () => void;
}) {
  return (
    <section className="mt-10 rounded-lg border border-destructive/50 bg-card">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4 p-5">
        <div className="min-w-0">
          <div className="font-mono text-[10px] font-semibold uppercase tracking-widest text-destructive">
            Danger zone
          </div>
          <h2 className="mt-2 text-[15px] font-medium text-foreground">Delete this project</h2>
          <p className="mt-1 max-w-prose text-[13px] leading-relaxed text-muted-foreground">
            Permanently removes{" "}
            <code className="rounded-sm bg-muted px-1 py-0.5 font-mono text-[12px] text-foreground">
              {project.project_slug}
            </code>{" "}
            and its history. This can't be undone.
          </p>
          {hasEnvs && (
            <p className="mt-3 flex items-center gap-2 text-[12px] font-medium text-amber-600 dark:text-amber-400">
              <span
                aria-hidden
                className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-current"
              />
              Delete its environments above before you can remove the project.
            </p>
          )}
        </div>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={onDelete}
          disabled={hasEnvs}
          className="shrink-0"
        >
          Delete project
        </Button>
      </div>
    </section>
  );
}

function EnvsList({
  error,
  items,
  projectSlug,
  onRefetch,
  onCreate,
}: {
  error: Error | null;
  items: ReadonlyArray<EnvironmentRecord>;
  projectSlug: string;
  onRefetch: () => void;
  onCreate: () => void;
}) {
  if (error) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-destructive">
          load failed
        </div>
        <p className="mt-2 text-[14px] text-foreground">
          {error instanceof WardkeyAPIError ? error.message : error.message || "Request failed"}
        </p>
        <Button variant="outline" size="sm" onClick={onRefetch} className="mt-3">
          Retry
        </Button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card/40 p-12 text-center">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          no environments yet
        </div>
        <h2 className="mt-3 text-lg font-medium text-foreground">
          Add an environment to <code className="font-mono text-foreground">{projectSlug}</code>
        </h2>
        <p className="mx-auto mt-2 max-w-md text-[13px] leading-relaxed text-muted-foreground">
          Environments scope secrets — most teams start with{" "}
          <code className="font-mono text-foreground">staging</code> and{" "}
          <code className="font-mono text-foreground">production</code>.
        </p>
        <Button type="button" size="sm" onClick={onCreate} className="mt-5">
          New environment
          <span aria-hidden>→</span>
        </Button>
      </div>
    );
  }

  return (
    <ul className="resource-grid">
      {items.map((env) => (
        <li key={env.env_slug}>
          <Link
            to={`/projects/${encodeURIComponent(projectSlug)}/envs/${encodeURIComponent(env.env_slug)}/secrets`}
            className="resource-card"
          >
            <span className="project-icon">
              <Layers size={21} strokeWidth={1.5} />
            </span>
            <h2>{env.display_name || env.env_slug}</h2>
            <code>{env.env_slug}</code>
            <footer>
              <span>Manage secrets</span>
              <ArrowRight size={17} />
            </footer>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function ProjectDetailSkeleton() {
  return (
    <div className="mx-auto max-w-[1200px]">
      <div className="mb-8 flex items-end justify-between border-b border-border pb-5">
        <Skeleton className="h-7 w-40" />
        <Skeleton className="h-8 w-40" />
      </div>
      <div className="overflow-hidden rounded-lg border border-border">
        <Skeleton className="h-9 w-full rounded-none" />
        {Array.from({ length: 2 }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows have no stable id
          <Skeleton key={i} className="h-11 w-full rounded-none" />
        ))}
      </div>
    </div>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <title>Edit</title>
      <path d="M16.5 4.5a2.121 2.121 0 0 1 3 3L7 20l-4 1 1-4Z" />
      <path d="m13.5 7.5 3 3" />
    </svg>
  );
}

function ProjectErrorState({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const isNotFound = error instanceof WardkeyAPIError && error.status === 404;
  return (
    <div className="mx-auto max-w-[1200px]">
      <PageHeader title="Project" />
      <div className="rounded-lg border border-dashed border-border bg-card/40 p-10 text-center">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {isNotFound ? "not found" : "load failed"}
        </div>
        <h2 className="mt-3 text-lg font-medium text-foreground">
          {isNotFound ? "This project doesn't exist" : "Couldn't load this project"}
        </h2>
        <p className="mx-auto mt-2 max-w-md text-[13px] leading-relaxed text-muted-foreground">
          {isNotFound
            ? "It may have been deleted, or the slug in the URL is wrong."
            : error instanceof WardkeyAPIError
              ? error.message
              : error.message || "Request failed"}
        </p>
        <div className="mt-5 flex items-center justify-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/projects">Back to projects</Link>
          </Button>
          {!isNotFound && (
            <Button type="button" size="sm" onClick={onRetry}>
              Retry
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
